import * as React from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import useSWR, { useSWRConfig } from 'swr'
import { useCurrentTenantId, useTenantContext } from '@/components/tenant/tenant-context'
import {
  changeRoles,
  createRole,
  deleteRole,
  invite,
  listAccessChanges,
  listAllUsers,
  listInvitations,
  listRoles,
  listUsers,
  PeopleRequestError,
  removeAccess,
  sendInvitationAgain,
  setRoleMembers,
  updateRole,
  withdrawInvitation,
  type AccessChangePage,
  type InvitationPage,
  type PeoplePage,
  type Person,
  type RoleRow,
} from '@/lib/bff/people'

const swrOptions = { revalidateOnFocus: false, shouldRetryOnError: false } as const

/** Every People & access SWR key starts with this, then the tenant id. */
const PREFIX = 'bff/people'

function useToken() {
  const { getAccessTokenSilently } = useAuth0()
  return React.useCallback(async () => {
    const token = await getAccessTokenSilently()
    if (!token) throw new PeopleRequestError(401, null, {}, 'No access token available')
    return token
  }, [getAccessTokenSilently])
}

/* One page of the current tenant's venue users. Pass `enabled: false` to make no request. */
export function usePeopleUsers(page: number, enabled = true) {
  const token = useToken()
  const tenantId = useCurrentTenantId()
  const { data, error, isLoading, mutate } = useSWR<PeoplePage>(
    enabled ? [PREFIX, tenantId, 'users', page] : null,
    async ([, tenant, , p]: [string, string, string, number]) => listUsers(await token(), tenant, p),
    swrOptions,
  )
  return { page: data, error, isLoading, revalidate: () => mutate() }
}

/* Every venue user, for a role's members. */
export function useAllPeopleUsers(enabled: boolean) {
  const token = useToken()
  const tenantId = useCurrentTenantId()
  const { data, error, isLoading, mutate } = useSWR<Person[]>(
    enabled ? [PREFIX, tenantId, 'all-users'] : null,
    async ([, tenant]: [string, string]) => listAllUsers(await token(), tenant),
    swrOptions,
  )
  return { people: data, error, isLoading, revalidate: () => mutate() }
}

export function useInvitations(page: number, enabled: boolean) {
  const token = useToken()
  const tenantId = useCurrentTenantId()
  const { data, error, isLoading, mutate } = useSWR<InvitationPage>(
    enabled ? [PREFIX, tenantId, 'invitations', page] : null,
    async ([, tenant, , p]: [string, string, string, number]) => listInvitations(await token(), tenant, p),
    swrOptions,
  )
  return { page: data, error, isLoading, revalidate: () => mutate() }
}

export function useRoles(enabled = true) {
  const token = useToken()
  const tenantId = useCurrentTenantId()
  const { data, error, isLoading, mutate } = useSWR<RoleRow[]>(
    enabled ? [PREFIX, tenantId, 'roles'] : null,
    async ([, tenant]: [string, string]) => (await listRoles(await token(), tenant)).data,
    swrOptions,
  )
  return { roles: data, error, isLoading, revalidate: () => mutate() }
}

export function useAccessChanges(page: number) {
  const token = useToken()
  const tenantId = useCurrentTenantId()
  const { data, error, isLoading, mutate } = useSWR<AccessChangePage>(
    [PREFIX, tenantId, 'changes', page],
    async ([, tenant, , p]: [string, string, string, number]) => listAccessChanges(await token(), tenant, p),
    swrOptions,
  )
  return { page: data, error, isLoading, revalidate: () => mutate() }
}

/*
 * The People & access mutations for the current tenant. Each revalidates
 * every People & access list of the tenant once it succeeds (users, roles,
 * invitations and the access-change record all move together). A role change
 * or removal also re-checks the caller's access decision.
 */
export function usePeopleMutations() {
  const token = useToken()
  const tenantId = useCurrentTenantId()
  const { refreshAccess } = useTenantContext()
  const { mutate } = useSWRConfig()

  return React.useMemo(() => {
    const revalidate = () =>
      mutate((key) => Array.isArray(key) && key[0] === PREFIX && key[1] === tenantId, undefined, { revalidate: true })
    const then = async <T,>(run: Promise<T>, accessMayChange = false): Promise<T> => {
      const result = await run
      void revalidate()
      if (accessMayChange) refreshAccess()
      return result
    }
    return {
      /** Reloads the tenant's People & access lists, e.g. after a refusal shows the data was stale. */
      revalidate,
      changeRoles: async (userId: string, roles: string[]) => then(changeRoles(await token(), tenantId, userId, roles), true),
      removeAccess: async (userId: string) => then(removeAccess(await token(), tenantId, userId), true),
      invite: async (request: { email: string; name?: string; roles: string[] }) => then(invite(await token(), tenantId, request)),
      withdraw: async (invitationId: string) => then(withdrawInvitation(await token(), tenantId, invitationId)),
      sendAgain: async (invitationId: string) => then(sendInvitationAgain(await token(), tenantId, invitationId)),
      createRole: async (request: { name: string; actions: string[] }) => then(createRole(await token(), tenantId, request)),
      updateRole: async (roleId: string, request: { name: string; actions: string[] }) =>
        then(updateRole(await token(), tenantId, roleId, request), true),
      deleteRole: async (roleId: string) => then(deleteRole(await token(), tenantId, roleId), true),
      setRoleMembers: async (roleId: string, userIds: string[]) =>
        then(setRoleMembers(await token(), tenantId, roleId, userIds), true),
    }
  }, [mutate, refreshAccess, tenantId, token])
}
